import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Keeps a list's view state in the URL (00-ds-09 §6, D148): tab, search, date
 * range, filters, sort and page — so a link is shareable, reload keeps the
 * place and Back works. Client-side only; reads/writes `useSearchParams`.
 *
 *   const [s, patch] = useListUrlState({ tab: 'posted', page: '1' });
 *   patch({ q: 'delta', page: '1' });   // sets ?tab=posted&q=delta&page=1
 */
export function useListUrlState<K extends string>(
  defaults: Record<K, string>,
): [Record<K, string>, (next: Partial<Record<K, string>>) => void] {
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => {
    const out = {} as Record<K, string>;
    (Object.keys(defaults) as K[]).forEach((k) => {
      out[k] = params.get(k) ?? defaults[k];
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const patch = useCallback(
    (next: Partial<Record<K, string>>) => {
      setParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          (Object.keys(next) as K[]).forEach((k) => {
            const v = next[k];
            if (v === undefined || v === '' || v === defaults[k]) sp.delete(k);
            else sp.set(k, v);
          });
          return sp;
        },
        { replace: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setParams],
  );

  return [state, patch];
}
